import {
  ENGAGEMENT_POPUP_VARIANTS,
  EngagementPopupOptions,
  NEW_MAIN_PAGE_BLOCK_TYPES,
  NewMainPageBlocksOrderConfig,
  NewMainPageBlockType,
  PopupOptions,
  TRoundingRules,
  YandexMetrikaFeatures,
} from '@web-catalog/types/omicron';

import {FieldValidator, isMap} from '@utils/omicron';

const isRoundingRule = (value: unknown): value is TRoundingRules[number] => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'min' in value && 'text' in value;
};

export const RoundingRulesValidator: FieldValidator<TRoundingRules | boolean> =
  {
    is: (value): value is TRoundingRules | boolean => {
      if (typeof value === 'boolean') {
        return true;
      }
      if (Array.isArray(value)) {
        return value.every(isRoundingRule);
      }
      return false;
    },
    fallback: false,
  };

const isNewMainPageBlockType = (
  value: unknown,
): value is NewMainPageBlockType => {
  return (
    typeof value === 'string' &&
    (NEW_MAIN_PAGE_BLOCK_TYPES as ReadonlyArray<string>).includes(value)
  );
};

export const NewMainPageBlocksOrderConfigValidator: FieldValidator<NewMainPageBlocksOrderConfig> =
  {
    is: (value): value is NewMainPageBlocksOrderConfig => {
      if (typeof value !== 'object') {
        return false;
      }
      return (
        'MAIN' in value &&
        'GAMES' in value &&
        Array.isArray(value.MAIN) &&
        value.MAIN.every(isNewMainPageBlockType) &&
        Array.isArray(value.GAMES) &&
        value.GAMES.every(isNewMainPageBlockType)
      );
    },
    fallback: {
      MAIN: [
        'selection_with_app_cards',
        'top_apps',
        'selection_cards',
        'selection_with_app_list',
        'articles',
      ],
      GAMES: [
        'selection_with_app_cards',
        'top_apps',
        'selection_cards',
        'selection_with_app_list',
        'articles',
      ],
    },
  };

const defaultYandexMetrikaFeatures: YandexMetrikaFeatures = {
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
  webvisor: true,
};

export const YandexMetricaFeaturesValidator: FieldValidator<YandexMetrikaFeatures> =
  {
    is: (value): value is YandexMetrikaFeatures => {
      if (!isMap(value)) {
        return false;
      }
      return Object.keys(defaultYandexMetrikaFeatures).every(
        (key) => key in value && typeof value[key] === 'boolean',
      );
    },
    fallback: defaultYandexMetrikaFeatures,
  };

export const PopupOptionsValidator: FieldValidator<PopupOptions> = {
  is: (value): value is PopupOptions => {
    if (!isMap(value)) {
      return false;
    }
    if (typeof value.enabled !== 'boolean') {
      return false;
    }

    if (!value.enabled) {
      return true;
    }
    const areMandatoryFieldsValid =
      typeof value.showAfterMillis === 'number' &&
      typeof value.showAfterScroll === 'boolean';

    if (!areMandatoryFieldsValid) {
      return false;
    }

    const isRepeatedShowValid =
      !('repeatedShowAfterSeconds' in value) ||
      typeof value.repeatedShowAfterSeconds === 'number';
    const areAppTypesValid =
      !('appTypes' in value) ||
      (Array.isArray(value.appTypes) &&
        value.appTypes.every(
          (type: unknown) =>
            typeof type === 'string' && ['MAIN', 'GAMES'].includes(type),
        ));

    return isRepeatedShowValid && areAppTypesValid;
  },
  fallback: {enabled: false},
};

export const EngagementPopupOptionsValidator: FieldValidator<EngagementPopupOptions> =
  {
    is: (value): value is EngagementPopupOptions => {
      const isBase = PopupOptionsValidator.is(value);

      return (
        isBase &&
        (!value.enabled ||
          ('variant' in value &&
            typeof value.variant === 'string' &&
            (ENGAGEMENT_POPUP_VARIANTS as readonly string[]).includes(
              value.variant,
            )))
      );
    },
    fallback: {enabled: false},
  };
